import React, { useState, useEffect } from 'react';
import cloneDeep from 'lodash/cloneDeep';

import { Table, Input, Button, Popconfirm, Select, InputNumber } from 'antd';

import StockService from 'services/stock';
import { errHandling } from 'utils';

const { Option } = Select;

export const EditableTable = ({
  dataSource = [],
  setDataSource,
  warehouseList,
  flash_sale_id,
  setMapListDeletedProduct,
}) => {
  const [listSelectedWarehouseID, setListSelectedWarehouseID] = useState([]);

  useEffect(() => {
    if (dataSource.length) {
      const cloned = cloneDeep(dataSource);
      const newListSelected = cloned.map((p, idx) => {
        let selectedWarehouseID;

        if (listSelectedWarehouseID[idx] !== undefined) {
          selectedWarehouseID =
            listSelectedWarehouseID[idx].selectedWarehouseID;
        } else {
          let selectedWarehouse =
            p.flash_sale_stocks.find(
              (e) => +e.product_collection_id === +flash_sale_id
            ) || {};
          selectedWarehouseID = selectedWarehouse.warehouse_id;
        }
        return { selectedWarehouseID };
      });
      setListSelectedWarehouseID(newListSelected);
    }
    // eslint-disable-next-line
  }, [dataSource.length, flash_sale_id]);

  const onFlashsaleDiscountChange = (index, discountType) => (evt) => {
    if (+evt.target.value > 100 && discountType === 'deduction_percentage') {
      return;
    }
    const newData = cloneDeep(dataSource);

    const current = getCurrentFlashsaleDiscount(newData[index]);
    current[discountType] = evt.target.value;

    setDataSource(newData);
  };

  const onDiscountTypeChange = (index) => (value) => {
    const newData = cloneDeep(dataSource);
    const reset =
      value === 'fixed_price' ? 'deduction_percentage' : 'fixed_price';

    const current = getCurrentFlashsaleDiscount(newData[index]);
    current[value] = 0;
    current[reset] = null;

    setDataSource(newData);
  };

  const onChangeWarehouseLocation = (index) => async (value) => {
    try {
      const newData = cloneDeep(dataSource);
      const currentProductID = newData[index].id;

      const {
        data: { data },
      } = await StockService.getProductStock(currentProductID);

      const hasStock =
        data.warehouse_stocks &&
        data.warehouse_stocks.some(({ quantity, warehouse }) => {
          if (warehouse.id === +value && quantity > 0) {
            return true;
          }

          return false;
        });

      if (!hasStock) {
        throw new Error('no stock in selected warehouse');
      }

      const clonedListSelectedWarehouseID = cloneDeep(listSelectedWarehouseID);
      const current = getCurrentFlashsaleStock(newData[index], +value);

      clonedListSelectedWarehouseID[index] = { selectedWarehouseID: +value };
      setListSelectedWarehouseID(clonedListSelectedWarehouseID);

      if (!current.product_collection_id) {
        newData[index].flash_sale_stocks.push({
          product_collection_id: +flash_sale_id,
          warehouse_id: +value,
        });
      }

      setDataSource(newData);
    } catch (error) {
      errHandling({ response: { data: { message: error.message } } });
    }
  };

  const onChangeFlashsaleStock = (index, currentWarehouseId) => (value) => {
    const newData = cloneDeep(dataSource);
    const current = getCurrentFlashsaleStock(
      newData[index],
      currentWarehouseId
    );
    current.quantity = value;
    setDataSource(newData);
  };

  const onMaxPurchaseChange = (index, currentWarehouseId) => (value) => {
    const newData = cloneDeep(dataSource);
    const current = getCurrentFlashsaleStock(
      newData[index],
      currentWarehouseId
    );
    current.max_purchase = value;
    setDataSource(newData);
  };

  const handleDelete = (product_id) => {
    setMapListDeletedProduct((prev) => {
      const cloned = cloneDeep(prev);
      if (cloned[product_id]) {
        cloned[product_id]._destroy = true;
      }
      return cloned;
    });
    setDataSource((prev) => {
      const filtered = prev.filter((item) => item.id !== product_id);
      return filtered;
    });
  };

  const popConfirmElement = (record) => (
    <Popconfirm
      title="Sure to delete?"
      onConfirm={() => handleDelete(record.id)}>
      <Button type="danger">Delete</Button>
    </Popconfirm>
  );

  popConfirmElement.displayName = 'PopConfirmElement';

  const infoProduct = (row) => (
    <div>
      <img
        style={{ width: '50px', height: '50px' }}
        src={row.preview.thumbnail_image}
        alt="thumbnail"
      />
      <div>{row.title}</div>
    </div>
  );

  const discount = ({ discountType, index, current }) => (
    <Input
      addonBefore={
        <Select
          defaultValue={discountType}
          onChange={onDiscountTypeChange(index, discountType)}>
          <Option value="deduction_percentage">%</Option>
          <Option value="fixed_price">Rp</Option>
        </Select>
      }
      value={
        discountType === 'fixed_price'
          ? current.fixed_price
          : current.deduction_percentage
      }
      onChange={onFlashsaleDiscountChange(index, discountType)}
    />
  );

  const discountPrice = ({ discountAmount, basePrice }) => (
    <span>
      {+discountAmount === 0 || isNaN(discountAmount)
        ? basePrice
        : (+basePrice - +discountAmount).toFixed()}
    </span>
  );

  const warehouseLocation = ({ currentWarehouseId, index }) => (
    <Select
      defaultValue={currentWarehouseId}
      value={currentWarehouseId}
      onChange={onChangeWarehouseLocation(index)}>
      {warehouseList.map(({ id, title }) => (
        <Option value={id} key={id}>
          {title}
        </Option>
      ))}
    </Select>
  );

  const flashsaleStock = ({
    currentFlashSaleAmount,
    index,
    currentWarehouseId,
  }) => (
    <InputNumber
      style={{ width: '60px' }}
      value={currentFlashSaleAmount}
      min={0}
      max={100000}
      onChange={onChangeFlashsaleStock(index, currentWarehouseId)}
    />
  );

  const maxPurchase = ({
    currentFlashsaleMaxPurchase,
    index,
    currentWarehouseId,
  }) => (
    <InputNumber
      style={{ width: '60px' }}
      value={currentFlashsaleMaxPurchase}
      min={1}
      max={100000}
      onChange={onMaxPurchaseChange(index, currentWarehouseId)}
    />
  );

  infoProduct.displayName = 'InfoProduct';
  discount.displayName = 'Discount';
  discountPrice.displayName = 'discountPrice';
  warehouseLocation.displayName = 'warehouseLocation';

  const getCurrentFlashsaleStock = (record, currentWarehouseId) => {
    const flash_sale_stocks = record.flash_sale_stocks || [];

    const currentFlashsale = flash_sale_stocks.find(
      ({ product_collection_id, warehouse_id }) =>
        product_collection_id === +flash_sale_id &&
        +currentWarehouseId === +warehouse_id
    );
    return currentFlashsale || {};
  };

  const getCurrentFlashsaleDiscount = (record) => {
    const flash_sale_discounts = record.flash_sale_discounts || [];
    const currentDiscount = flash_sale_discounts.find(
      ({ product_collection_id }) => product_collection_id === +flash_sale_id
    );

    return currentDiscount || {};
  };

  const columns = [
    {
      title: 'Info Product',
      dataIndex: 'name',
      // width: '20%',
      render: (_, row) => {
        return infoProduct(row);
      },
    },
    {
      title: 'Normal Price',
      dataIndex: 'price',
    },
    {
      title: 'Discount',
      dataIndex: 'flashsale_discount',
      render: (_, record, index) => {
        const current = getCurrentFlashsaleDiscount(record);
        const discountType =
          current.fixed_price !== null ? 'fixed_price' : 'deduction_percentage';

        return discount({ discountType, index, current });
      },
    },
    {
      title: 'Discount Price',
      dataIndex: 'flashsale_price',
      render: (_, record) => {
        const current = getCurrentFlashsaleDiscount(record);
        const discountType = current.fixed_price
          ? 'fixed_price'
          : 'deduction_percentage';
        const basePrice = record.price;
        const discountAmount =
          discountType === 'fixed_price'
            ? +current.fixed_price
            : +basePrice * (+current.deduction_percentage / 100);
        return discountPrice({ discountAmount, basePrice });
      },
    },
    {
      title: 'Warehouse Location',
      dataIndex: 'flashsale_warehouse',
      render: (_, __, index) => {
        let currentWarehouseId = null;

        if (listSelectedWarehouseID[index] !== undefined) {
          currentWarehouseId =
            listSelectedWarehouseID[index].selectedWarehouseID;
        }

        return warehouseLocation({ index, currentWarehouseId });
      },
    },
    {
      title: 'Flash Sale Stock',
      dataIndex: 'flashsale_stock',
      render: (_, record, index) => {
        let currentWarehouseId = null;

        if (listSelectedWarehouseID[index] !== undefined) {
          currentWarehouseId =
            listSelectedWarehouseID[index].selectedWarehouseID;
        }
        const currentFlashsale = getCurrentFlashsaleStock(
          record,
          currentWarehouseId
        );
        const currentFlashSaleAmount = currentFlashsale.quantity;

        return flashsaleStock({
          index,
          currentFlashSaleAmount,
          currentWarehouseId,
        });
      },
    },
    {
      title: 'Max Purchase',
      dataIndex: 'flashsale_max_purchase',
      render: (_, record, index) => {
        let currentWarehouseId = null;

        if (listSelectedWarehouseID[index] !== undefined) {
          currentWarehouseId =
            listSelectedWarehouseID[index].selectedWarehouseID;
        }
        const currentFlashsale = getCurrentFlashsaleStock(
          record,
          currentWarehouseId
        );
        const currentFlashsaleMaxPurchase = currentFlashsale.max_purchase;

        return maxPurchase({
          index,
          currentFlashsaleMaxPurchase,
          currentWarehouseId,
        });
      },
    },
    {
      title: 'Action',
      render: (text, record) => {
        // eslint-disable-next-line react/display-name
        return popConfirmElement(record);
      },
    },
  ];

  return (
    <div style={{ padding: 20 }}>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={dataSource}
        pagination={false}
      />
    </div>
  );
};
